import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trophy, Calendar, MapPin, Gauge, Loader2, AlertCircle } from "lucide-react";
import {
  formatGameType,
  formatKickoff,
  formatVenue,
  groupByRound,
  pickLogo,
} from "@/lib/playhq";

/**
 * Member Fixtures — powered by PlayHQ.
 *
 * Flow:
 *   1. Load `/playhq/seasons` and pick the newest non-COMPLETED season.
 *   2. Load `/playhq/seasons/{id}/club-fixtures?status=UPCOMING` — this
 *      backend endpoint aggregates every UPCOMING fixture across every
 *      grade a Derwent team is registered in and returns them in one
 *      round-trip alongside team + grade lookup maps.
 *   3. Group fixtures by `round_name` and render as a flat list of
 *      PlayHQ-style score cards. No admin-side DCC fixture data.
 */
export default function MemberFixtures() {
  const [seasonMeta, setSeasonMeta] = useState(null);
  const [fixtures, setFixtures] = useState([]);
  const [teamMap, setTeamMap] = useState({});
  const [gradeMap, setGradeMap] = useState({});
  const [clubTeamIds, setClubTeamIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeGradeId, setActiveGradeId] = useState("all");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const seasonsRes = await api.get("/playhq/seasons");
        const seasons = Array.isArray(seasonsRes.data) ? seasonsRes.data : [];
        // Newest non-COMPLETED season, else fall back to the newest overall.
        const preferred = seasons.find((s) => s.status && s.status !== "COMPLETED") || seasons[0];
        if (!preferred) {
          if (!cancelled) {
            setSeasonMeta(null);
            setFixtures([]);
            setLoading(false);
          }
          return;
        }
        if (cancelled) return;
        setSeasonMeta(preferred);

        const { data } = await api.get(`/playhq/seasons/${preferred.playhq_id}/club-fixtures`, {
          params: { status: "UPCOMING" },
        });
        if (cancelled) return;
        setFixtures(Array.isArray(data?.fixtures) ? data.fixtures : []);
        setTeamMap(data?.team_map || {});
        setGradeMap(data?.grade_map || {});
        setClubTeamIds(new Set(Array.isArray(data?.club_team_ids) ? data.club_team_ids : []));
      } catch (err) {
        if (cancelled) return;
        const msg = err?.response?.data?.detail || "Failed to load PlayHQ fixtures";
        setError(msg);
        toast.error(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const rounds = useMemo(() => {
    const filtered = activeGradeId === "all"
      ? fixtures
      : fixtures.filter((f) => f.grade_id === activeGradeId);
    return groupByRound(filtered);
  }, [fixtures, activeGradeId]);

  // Compact chip labels — strip the common "SCA " prefix / " Grade" suffix
  // so cards fit on mobile without wrapping.
  const shortGradeLabel = (name) => {
    if (!name) return "Grade";
    return name
      .replace(/^SCA\s+/i, "")
      .replace(/\s+Grade$/i, "")
      .trim() || name;
  };

  const gradeChips = useMemo(() => {
    // Only expose grades that actually have upcoming fixtures loaded, so
    // members never tap a chip that would land on an empty list.
    const inUse = new Set(fixtures.map((f) => f.grade_id));
    const items = Object.values(gradeMap)
      .filter((g) => g && g.id && inUse.has(g.id))
      .map((g) => ({ id: g.id, label: shortGradeLabel(g.name), full: g.name }));
    items.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
    return items;
  }, [gradeMap, fixtures]);

  // If the current chip disappears (e.g. season reload), fall back to "All".
  useEffect(() => {
    if (activeGradeId !== "all" && !gradeChips.some((g) => g.id === activeGradeId)) {
      setActiveGradeId("all");
    }
  }, [gradeChips, activeGradeId]);

  return (
    <AppShell variant="member" title="Fixtures">
      <div className="space-y-6">
        {/* Season header */}
        {seasonMeta && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Current season</div>
            <h2 className="text-lg font-semibold tracking-tight" data-testid="member-fixtures-season-name">
              {seasonMeta.name}
            </h2>
          </div>
        )}

        {/* Grade filter chips */}
        {!loading && !error && seasonMeta && fixtures.length > 0 && gradeChips.length > 0 && (
          <div
            className="-mx-1 flex flex-wrap gap-2"
            role="tablist"
            aria-label="Filter fixtures by grade"
            data-testid="member-fixtures-grade-chips"
          >
            <GradeChip
              label="All"
              testId="member-fixtures-grade-chip-all"
              active={activeGradeId === "all"}
              onClick={() => setActiveGradeId("all")}
              count={fixtures.length}
            />
            {gradeChips.map((g) => (
              <GradeChip
                key={g.id}
                label={g.label}
                title={g.full}
                testId={`member-fixtures-grade-chip-${g.id}`}
                active={activeGradeId === g.id}
                onClick={() => setActiveGradeId(g.id)}
                count={fixtures.filter((f) => f.grade_id === g.id).length}
              />
            ))}
          </div>
        )}

        {/* States */}
        {loading ? (
          <Card className="p-10 rounded-2xl text-center text-sm text-muted-foreground" data-testid="member-fixtures-loading">
            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
            <div className="mt-2">Loading upcoming fixtures…</div>
          </Card>
        ) : error ? (
          <Card className="p-8 rounded-2xl text-center max-w-lg mx-auto" data-testid="member-fixtures-error">
            <div className="mx-auto h-12 w-12 rounded-2xl bg-[hsl(var(--secondary))] flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="mt-4 text-base font-semibold tracking-tight">Couldn&apos;t load fixtures</h3>
            <p className="mt-2 text-sm text-muted-foreground">{error}</p>
          </Card>
        ) : !seasonMeta ? (
          <Card className="p-10 rounded-2xl text-center" data-testid="member-fixtures-no-season">
            <div className="mx-auto h-10 w-10 rounded-xl bg-[hsl(var(--secondary))] flex items-center justify-center">
              <Trophy className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="mt-3 text-sm font-semibold">No current season yet</div>
            <p className="mt-1 text-xs text-muted-foreground max-w-xs mx-auto">
              PlayHQ hasn&apos;t published a current season yet. Check back once fixtures are released.
            </p>
          </Card>
        ) : fixtures.length === 0 ? (
          <Card className="p-10 rounded-2xl text-center" data-testid="member-fixtures-empty">
            <div className="mx-auto h-10 w-10 rounded-xl bg-[hsl(var(--secondary))] flex items-center justify-center">
              <Trophy className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="mt-3 text-sm font-semibold">No upcoming fixtures</div>
            <p className="mt-1 text-xs text-muted-foreground max-w-xs mx-auto">
              Every {seasonMeta.name} match for Derwent&apos;s teams has already been played. Check Results to review completed games.
            </p>
          </Card>
        ) : rounds.length === 0 ? (
          <Card className="p-10 rounded-2xl text-center" data-testid="member-fixtures-grade-empty">
            <div className="mx-auto h-10 w-10 rounded-xl bg-[hsl(var(--secondary))] flex items-center justify-center">
              <Trophy className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="mt-3 text-sm font-semibold">No matches for this grade</div>
            <p className="mt-1 text-xs text-muted-foreground max-w-xs mx-auto">
              Try another grade, or tap <span className="font-medium text-foreground">All</span> to see everything upcoming.
            </p>
          </Card>
        ) : (
          <div className="space-y-6" data-testid="member-fixtures-rounds">
            {rounds.map((round) => (
              <div key={round.name} data-testid={`member-fixtures-round-${round.name.replace(/\s+/g, "-")}`}>
                <div className="flex items-baseline justify-between mb-2 px-1">
                  <h3 className="text-sm font-semibold tracking-tight text-foreground/80">
                    {round.name}
                  </h3>
                  <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                    {round.games.length} game{round.games.length === 1 ? "" : "s"}
                  </span>
                </div>
                <ul className="space-y-3">
                  {round.games.map((game) => (
                    <UpcomingFixtureRow
                      key={game.playhq_id}
                      game={game}
                      teamMap={teamMap}
                      gradeMap={gradeMap}
                      clubTeamIds={clubTeamIds}
                    />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// Fixture row: mirrors the PlayHQ Results card layout but without scores,
// so the emphasis stays on kickoff time + venue for match planning.
// ---------------------------------------------------------------------------
function UpcomingFixtureRow({ game, teamMap, gradeMap, clubTeamIds }) {
  const home = (game.teams || []).find((t) => t.isHomeTeam) || (game.teams || [])[0] || {};
  const away = (game.teams || []).find((t) => !t.isHomeTeam) || (game.teams || [])[1] || {};
  const involvesClub = (game.teams || []).some((t) => clubTeamIds.has(t.id));

  const kickoff = formatKickoff(game.start_at);
  const venueLabel = formatVenue(game.venue);
  const format = formatGameType(game.type);
  const gradeName = gradeMap[game.grade_id]?.name;

  return (
    <li
      className={`rounded-2xl border overflow-hidden ${
        involvesClub
          ? "border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/[0.04]"
          : "border-border bg-[hsl(var(--card))]"
      }`}
      data-testid={`member-fixture-card-${game.playhq_id}`}
    >
      <div className="flex items-center justify-end gap-1.5 px-4 pt-3">
        <Badge
          variant="outline"
          className="text-[10px] font-semibold uppercase tracking-wider bg-blue-500/10 text-blue-600 border-blue-500/30"
          data-testid={`member-fixture-status-${game.playhq_id}`}
        >
          Upcoming
        </Badge>
      </div>

      <div className="px-4 pb-4 pt-1 grid grid-cols-[1fr_auto_auto_1fr] items-center gap-x-2 sm:gap-x-4">
        <FixtureTeam teamRef={home} teamMap={teamMap} clubTeamIds={clubTeamIds} align="right" />
        <div className="text-center text-xl sm:text-2xl font-mono font-medium text-muted-foreground/60">vs</div>
        <div />
        <FixtureTeam teamRef={away} teamMap={teamMap} clubTeamIds={clubTeamIds} align="left" />
      </div>

      <div className="border-t border-border/60 bg-[hsl(var(--bg))]/40 px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
        {format && (
          <span className="inline-flex items-center gap-1.5" data-testid={`member-fixture-format-${game.playhq_id}`}>
            <Gauge className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
            <span className="text-foreground/70">{format}</span>
          </span>
        )}
        <span className="inline-flex items-center gap-1.5" data-testid={`member-fixture-kickoff-${game.playhq_id}`}>
          <Calendar className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
          <span className="text-foreground/70">{kickoff}</span>
        </span>
        {venueLabel && (
          <span className="inline-flex items-center gap-1.5" data-testid={`member-fixture-venue-${game.playhq_id}`}>
            <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
            <span className="text-foreground/70">{venueLabel}</span>
          </span>
        )}
        {gradeName && (
          <span className="inline-flex items-center gap-1.5" data-testid={`member-fixture-grade-${game.playhq_id}`}>
            <Trophy className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
            <span className="text-foreground/70">{gradeName}</span>
          </span>
        )}
      </div>
    </li>
  );
}

function FixtureTeam({ teamRef, teamMap, clubTeamIds, align = "left" }) {
  const team = teamMap[teamRef?.id];
  const name = team?.name || "TBC";
  const logo = pickLogo(team?.raw);
  const isClub = clubTeamIds.has(teamRef?.id);
  const nameCls = `text-sm sm:text-base truncate ${isClub ? "font-semibold" : "font-medium"} text-foreground/85`;
  const LogoEl = logo ? (
    <img
      src={logo}
      alt=""
      className="h-9 w-9 sm:h-10 sm:w-10 rounded-full object-cover shrink-0 bg-[hsl(var(--secondary))]"
      loading="lazy"
    />
  ) : (
    <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-[hsl(var(--secondary))] shrink-0" />
  );
  const nameEl = <div className={`${nameCls} ${align === "right" ? "text-right" : ""}`}>{name}</div>;
  return (
    <div className={`flex items-center gap-2 sm:gap-3 min-w-0 ${align === "right" ? "flex-row justify-end" : "flex-row justify-start"}`}>
      {align === "right" && nameEl}
      {LogoEl}
      {align === "left" && nameEl}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Grade filter chip — a small pill that toggles the visible grade. Uses the
// same accent tokens as the rest of the app so it feels native next to the
// PlayHQ result cards.
// ---------------------------------------------------------------------------
function GradeChip({ label, count, active, onClick, testId, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      role="tab"
      aria-selected={active}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent))]/40 ${
        active
          ? "bg-[hsl(var(--accent))] border-[hsl(var(--accent))] text-white"
          : "bg-[hsl(var(--card))] border-border text-foreground/80 hover:border-foreground/40 hover:text-foreground"
      }`}
    >
      <span className="truncate max-w-[10rem]">{label}</span>
      {typeof count === "number" && (
        <span
          className={`min-w-[1.25rem] h-5 px-1.5 inline-flex items-center justify-center rounded-full text-[10px] tabular-nums ${
            active ? "bg-white/20 text-white" : "bg-[hsl(var(--secondary))] text-foreground/70"
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );
}
